import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import { packChatPreview } from "@/payroll/pack-chat/message-preview";
import { PackChatStatusMark } from "@/payroll/pack-chat/pack-chat-status-mark";
import { referenceParts } from "@/payroll/pack-chat/reference-label";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";

/** One question in the manager's thread list. */
export const PackChatThreadRow = ({
  thread,
  selected,
  onSelect,
}: {
  thread: IPackChatThreadSummary;
  selected: boolean;
  onSelect: () => void;
}) => {
  const { label } = referenceParts(thread.context);
  const open = thread.status === "open";
  const last = thread.last_message;
  const preview = last ? packChatPreview(last) : null;

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        "w-full px-4 py-3 text-left transition-colors hover:bg-surface-secondary",
        { "bg-surface-secondary": selected },
      )}
    >
      <Stack gap="none" align="stretch" fullWidth className="min-w-0">
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap="xs"
          fullWidth
        >
          <Typography
            variant="bodyW3"
            textColor="tertiary"
            className="min-w-0 truncate text-[11px] uppercase tracking-wide"
          >
            {label}
          </Typography>
          <span className="shrink-0">
            <PackChatStatusMark
              focusable={false}
              hasAiMessages={thread.has_ai_messages}
              needsManager={thread.needs_manager}
              responseOwner={thread.response_owner}
              open={open}
              label={open ? "Open" : "Closed"}
            />
          </span>
        </Stack>

        {last && (
          <Typography
            variant="bodyW3"
            className={cn("line-clamp-2 pt-0.5 text-[13px]", {
              "text-secondary-text": !open,
            })}
          >
            {preview?.author && (
              <span className="font-medium">{preview.author}:</span>
            )}{" "}
            {preview?.body}
          </Typography>
        )}

        <Typography
          variant="bodyW3"
          textColor="tertiary"
          className="pt-1 text-[12px]"
        >
          {dayjs(thread.updated_at).fromNow()}
        </Typography>
      </Stack>
    </button>
  );
};
